import { type ThunkAction, type ThunkDispatch } from "@reduxjs/toolkit";
import { toDraftRestaurant } from "dashboard/src/models/locations";

import type {
  NonPrimaryLocation,
  PrimaryLocation,
  RestaurantLocation,
} from "@dashboard/types/location";
import { type ReduxState } from "@dashboard/types/reduxState";
import { type ThunkExtraArgument } from "@dashboard/types/thunk";

import { type ErrorReducerAction } from "../reducers/errorReducer";
import type { LocationReducerAction } from "../reducers/locationReducer";
import { addError } from "./addErrorAction";
import { updateRestaurant } from "./restaurantActions";

// Types
type ThunkActionReturn = ThunkAction<
  Promise<void>,
  ReduxState,
  ThunkExtraArgument,
  LocationReducerAction
>;

export type LocationThunkDispatch = ThunkDispatch<
  ReduxState,
  ThunkExtraArgument,
  LocationReducerAction | ErrorReducerAction
>;

// Actions
export const newLocation: (
  location: NonPrimaryLocation
) => ThunkActionReturn = (location: NonPrimaryLocation) => {
  return (
    dispatch: LocationThunkDispatch,
    getState: () => ReduxState,
    { locationApi }: ThunkExtraArgument
  ) => {
    return locationApi
      .newLocation(location)
      .then(() => {
        dispatch({ type: "SET_LOCATION", payload: location });
      })
      .catch(addError(dispatch));
  };
};

export const updateLocation: (
  location: RestaurantLocation
) => ThunkActionReturn = (location: RestaurantLocation) => {
  return (
    dispatch: LocationThunkDispatch,
    getState: () => ReduxState,
    { locationApi }: ThunkExtraArgument
  ) => {
    if (location.isPrimary) {
      // use the restauranAction to update the restaurant
      const draftRestaurant = toDraftRestaurant(location as PrimaryLocation);
      return dispatch(updateRestaurant(draftRestaurant)).then(() => {
        dispatch({ type: "SET_LOCATION", payload: location });
      });
    } else {
      return locationApi
        .updateLocation(location) // as NonPrimaryLocation
        .then(() => {
          dispatch({ type: "SET_LOCATION", payload: location });
        })
        .catch(addError(dispatch));
    }
  };
};

export const deleteLocation: (
  restaurantUuid: string,
  locationId: string
) => ThunkActionReturn = (restaurantUuid: string, locationId: string) => {
  return (
    dispatch: LocationThunkDispatch,
    getState: () => ReduxState,
    { locationApi }: ThunkExtraArgument
  ) => {
    return locationApi
      .deleteLocation(restaurantUuid, locationId)
      .then(() => {
        dispatch({ type: "DELETE_LOCATION", payload: locationId });
      })
      .catch(addError(dispatch));
  };
};

export const fetchLocations: () => ThunkActionReturn = () => {
  return (
    dispatch: LocationThunkDispatch,
    getState: () => ReduxState,
    { locationApi }: ThunkExtraArgument
  ) => {
    // get current restaurant from state. this allows for running fetchDishes
    // as a refresh operation even when restaurants are changing.
    const currentRest = getState().restaurant.selectedRestaurant;
    if (!currentRest) {
      return Promise.resolve();
    }

    return locationApi
      .fetchLocations(currentRest)
      .then((locations) => {
        dispatch({
          type: "FETCHED_LOCATIONS",
          payload: locations,
        });
      })
      .catch(addError(dispatch));
  };
};
